import { useCallback, useSyncExternalStore } from "react";
import type { DockviewApi } from "dockview-react";
import {
  ALL_ACCOUNTS,
  avgPrice,
  rithmicAsOf,
  useLinkGroups,
  useSettings,
  type ConnectionView,
} from "@helm/core";
import { contractRoot, formatAvgPrice, formatCents, formatMoney, pnlTone, positionText } from "@helm/ui";
import { marketData, priceFormatOf, type InstrumentFacts } from "../md/marketData";
import { usePickerView, useStripFigures, useTradingViewSlice, type TradingView } from "../viewmodel/account";
import { shallowEqual } from "../viewmodel/slice";
import { DdRoomFigure } from "../risk/RiskCoverage";
import { TopBarFlatten } from "../trading/TableActions";
import { workspaceActions } from "../workspace/Workspace";
import { useActiveChart } from "../toolbar/activeChart";
import "./statusLine.css";

/** The strip's position in its contract: a slice that fills change, quotes do not. */
type StripPosition = {
  contract: string | null;
  netQty: number;
  num: bigint | null;
  den: bigint | null;
  info: InstrumentFacts | null;
  accounts: number;
  exact: boolean;
};

const FLAT: StripPosition = {
  contract: null,
  netQty: 0,
  num: null,
  den: null,
  info: null,
  accounts: 0,
  exact: true,
};

/**
 * The selected account's open position in `symbol` (a root like "NQ" matches its contract
 * "NQZ6"); with All accounts selected, the net over every account. Several accounts' positions
 * have no single average, so it is left out then.
 */
function stripPositionOf(v: TradingView, symbol: string | null): StripPosition {
  if (symbol === null) return FLAT;
  const all = v.selection === ALL_ACCOUNTS;
  const root = contractRoot(symbol);
  const mine = v.positions.filter(
    (p) =>
      (all || p.accountId === v.activeId) &&
      p.netQty !== 0 &&
      (p.symbol === symbol || contractRoot(p.symbol) === root),
  );
  const first = mine[0];
  if (first === undefined) return FLAT;
  const rows = mine.filter((p) => p.symbol === first.symbol);
  const netQty = rows.reduce((n, p) => n + p.netQty, 0);
  const accounts = new Set(rows.map((p) => p.accountId)).size;
  const avg = rows.length === 1 ? avgPrice(first.position) : null;
  return {
    contract: first.symbol,
    netQty,
    num: avg?.num ?? null,
    den: avg?.den ?? null,
    info: first.info,
    accounts,
    exact: rows.every((p) => p.exactCost),
  };
}

/** The hub's resolved contract for a root ("NQ" → "NQZ6"), without re-rendering on its quotes. */
function useResolved(symbol: string | null): InstrumentFacts | null {
  const subscribe = useCallback(
    (l: () => void) => (symbol === null ? () => {} : marketData.listen(symbol, l)),
    [symbol],
  );
  return useSyncExternalStore(subscribe, () =>
    symbol === null ? null : (marketData.get(symbol)?.info ?? null),
  );
}

const money = (v: number | null, privacy: boolean): string =>
  v === null ? "—" : formatMoney(v, { privacy, signed: true, decimals: 2 });

/**
 * Position strip (owner 2026-10-04 UI clean-up), the row under the status line and the one home of
 * the trading account's figures: account · contract · Position ("+3 @ 21,450.25" or "Flat") ·
 * Balance · Open P&L · Day P&L · DD room · Working N (opens the Orders module) · … · the
 * account-level Flatten. Balance (owner 2026-10-05) is the Accounts table's Account Balance (the
 * Total row's sum with All accounts); it sits with the account's money, after the contract's
 * Position, so the contract and its position stay together. It starts with the trading account
 * ("APEX-50K-0142 · NQZ6"): the one selection every panel trades, set in the status line or in any
 * panel's account picker. Flatten acts on that account ("Flatten all" while All accounts is
 * selected). The contract is the active chart's, else the red link group's. Every money value
 * follows the privacy mask; unknown values show "—". Quotes re-render only the P&L figures.
 *
 * The workspace tabs, "+ Add module" and "Lock layout" (owner 2026-10-04: no separate workspace
 * row, the dock gets its height) come in as `children`, between Working and Flatten. The shell
 * creates that element, so the strip's quote-driven re-renders reuse it and never re-render the
 * tabs.
 */
export function PositionStrip({
  connection,
  dock,
  children,
}: {
  connection: ConnectionView;
  dock: () => DockviewApi | null;
  /** The workspace bar (WorkspaceBar.tsx): it fills the space between the figures and Flatten. */
  children?: React.ReactNode;
}): React.JSX.Element {
  const chart = useActiveChart();
  const red = useLinkGroups((s) => s.groups.red.symbol);
  const symbol = chart?.snapshot.symbol ?? red;
  const select = useCallback((v: TradingView) => stripPositionOf(v, symbol), [symbol]);
  const pos = useTradingViewSlice(select, shallowEqual);
  const resolved = useResolved(symbol);
  const figures = useStripFigures();
  const privacy = useSettings((s) => s.settings.privacy);
  const shown = usePickerView();
  const account = shown.allSelected
    ? "All accounts"
    : shown.active === null
      ? "No account"
      : privacy
        ? "***"
        : shown.active.label;
  const contract = pos.contract ?? resolved?.symbol ?? symbol ?? "—";
  const fmt = priceFormatOf(pos.info ?? resolved);
  const avg =
    pos.num !== null && pos.den !== null ? formatAvgPrice({ num: pos.num, den: pos.den }, fmt) : null;
  const stale = connection.status !== "connected" && connection.sessionId !== null;
  const scope = figures.allSelected ? ", all accounts" : "";
  const working = figures.workingOrders;
  return (
    <div
      className="posstrip"
      role="group"
      aria-label="Position and account figures"
      data-stale={stale ? "1" : undefined}
    >
      <span className="posstrip-id">
        <span
          className="posstrip-account"
          data-figure="account"
          title="The trading account for every panel (set in the status line or in any panel's account picker)"
        >
          {account}
        </span>
        <span className="posstrip-sep" aria-hidden="true">
          ·
        </span>
        <span
          className="posstrip-contract num"
          title="The active chart's contract (else the red link group's)"
        >
          {contract}
        </span>
      </span>
      <span
        className="strip-kv"
        data-figure="position"
        title={
          pos.netQty === 0
            ? `No open position in ${contract}${scope}`
            : `Open position in ${contract}${scope}${pos.exact ? "" : " (average rounded by the gateway)"}`
        }
      >
        <span className="strip-label">Position</span>
        <span className={`strip-value num tone-${pos.netQty > 0 ? "pos" : pos.netQty < 0 ? "neg" : "none"}`}>
          {positionText(pos.netQty, avg, pos.accounts)}
        </span>
      </span>
      <span
        className="strip-kv"
        data-figure="balance"
        title={
          figures.balanceCents === null
            ? figures.balanceReason
            : `Rithmic account balance${figures.allSelected ? ", sum over all accounts" : ""}`
        }
      >
        <span className="strip-label">Balance</span>
        <span className="strip-value num tone-none">
          {figures.balanceCents === null ? "—" : formatCents(figures.balanceCents, { privacy })}
        </span>
      </span>
      <span
        className="strip-kv"
        data-figure="open-pnl"
        title={`Open (unrealized) P&L, ${rithmicAsOf(figures.unrealizedAsOfNs) || "Rithmic"}${scope}`}
      >
        <span className="strip-label">Open P&amp;L</span>
        <span className={`strip-value num tone-${pnlTone(figures.unrealizedPnl)}`}>
          {money(figures.unrealizedPnl, privacy)}
        </span>
      </span>
      <span
        className="strip-kv"
        data-figure="day-pnl"
        title={`Day P&L, ${rithmicAsOf(figures.dayPnlAsOfNs) || "reported by Rithmic"}${scope}`}
      >
        <span className="strip-label">Day P&amp;L</span>
        <span className={`strip-value num tone-${pnlTone(figures.dayNetPnl)}`}>
          {money(figures.dayNetPnl, privacy)}
        </span>
      </span>
      <DdRoomFigure connected={connection.status === "connected"} />
      <button
        type="button"
        className="strip-kv strip-kv-btn"
        data-figure="working"
        title={`Working orders${scope}: open the Orders module`}
        onClick={() => {
          const d = dock();
          if (d) workspaceActions.focusOrAdd(d, "orders");
        }}
      >
        <span className="strip-label">Working</span>
        <span className="strip-value num">{working === null ? "—" : String(working)}</span>
      </button>
      {stale && (
        <span className="posstrip-stale" title="The gateway is not connected: these are not live values">
          Last known
        </span>
      )}
      {children === undefined ? (
        <span className="spacer" />
      ) : (
        <>
          <span className="posstrip-divider" aria-hidden="true" />
          {children}
        </>
      )}
      <span className="posstrip-divider" aria-hidden="true" />
      <TopBarFlatten />
    </div>
  );
}
